import { useQuery } from '@apollo/client';
import { isToday } from 'date-fns';
import { HOME_SECTION_QUERY_OPTIONS } from '../sectionQueryOptions';
import { HomeWellbeingMeasureStatus } from '../WellbeingMeasure/__generated__/HomeWellbeingMeasureStatus';
import { WellbeingMeasureQuestionnaire } from '../WellbeingMeasure/__generated__/WellbeingMeasureQuestionnaire';
import {
  HOME_WELLBEING_MEASURE_STATUS_QUERY,
  WELLBEING_MEASURE_QUESTIONNAIRE_QUERY,
} from '../WellbeingMeasure/wellbeingMeasureQueries';
import { useSectionErrorLogging } from './useSectionErrorLogging';

export const useWellbeingMeasure = ({
  isSurveyOpen,
}: {
  isSurveyOpen: boolean;
}) => {
  const { data, loading, error } = useQuery<HomeWellbeingMeasureStatus>(
    HOME_WELLBEING_MEASURE_STATUS_QUERY,
    HOME_SECTION_QUERY_OPTIONS,
  );
  const isDue = Boolean(data?.wellbeingMeasureStatus?.shouldDisplay);
  const lastCompletedAt = data?.wellbeingMeasureStatus?.lastCompletedAt;
  const isCompletedToday = Boolean(
    lastCompletedAt && isToday(new Date(lastCompletedAt)),
  );

  // Kept running while the survey is open: a skipped query drops its data,
  // which would unmount the survey and lose the answers mid-way through.
  const isQuestionnaireSkipped = !isDue && !isSurveyOpen;
  const {
    data: questionnaireResult,
    loading: questionnaireLoading,
    error: questionnaireError,
  } = useQuery<WellbeingMeasureQuestionnaire>(
    WELLBEING_MEASURE_QUESTIONNAIRE_QUERY,
    { ...HOME_SECTION_QUERY_OPTIONS, skip: isQuestionnaireSkipped },
  );

  useSectionErrorLogging(error, data, 'wellbeing-measure');
  useSectionErrorLogging(
    questionnaireError,
    questionnaireResult,
    'wellbeing-measure-questionnaire',
  );

  const isLoading =
    (loading && !data) ||
    (!isQuestionnaireSkipped && questionnaireLoading && !questionnaireResult);

  return {
    isDue,
    isCompletedToday,
    hasCheckedInBefore: Boolean(lastCompletedAt),
    isLoading,
    questionnaire: questionnaireResult?.wellbeingMeasureQuestionnaire ?? null,
  };
};
